import { Injectable } from '@angular/core';
import { RequestService } from '@services/request/request.service';
import { tap, catchError, mapTo } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { Jwt } from './token-model';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Router } from '@angular/router';
import { ModalService } from '../services/modal/modal.service';
import { MenuService } from '../services/menu/menu.service';
import { OAUTH_AZURE_CONFIG, OAUTH_OKTA_CONFIG } from '@app/global/constants';

@Injectable()
export class AuthenticationService {
  readonly ROLES = 'SESSION_ROLE';
  public authType?: 'AZURE' | 'OKTA';

  constructor(
    private readonly requestService: RequestService,
    public oidcSecurityService: OidcSecurityService,
    public router: Router,
    private modalService: ModalService,
    private menuSrv: MenuService
  ) {}

  public get currentTokenValue(): string {
    try {
      let oktaSession = localStorage.getItem('OKTA_ROLES_CACHE');
      if (!oktaSession) {
        oktaSession = sessionStorage.getItem('OKTA_ROLES_CACHE');
      }
      if (oktaSession) {
        const parsed = JSON.parse(oktaSession);
        if (parsed?.authnResult?.access_token) {
          return parsed.authnResult.access_token;
        }
      }
      // Fallback en vivo, igual que Azure: por si el guard aún no ha
      // escrito OKTA_ROLES_CACHE (primera carga tras login).
      const oktaAccessToken = this.oidcSecurityService.getAccessToken('OKTA');
      if (oktaAccessToken) {
        return oktaAccessToken;
      }
      const azureIdToken = this.oidcSecurityService.getIdToken('AZURE');
      if (azureIdToken) {
        return azureIdToken;
      }

      return '';
    } catch (error) {
      return '';
    }
  }

  public get userEmail(): string {
    const token = this.currentTokenValue;
    if (!token) return '';
    try {
      const decoded = this.decodeJwt(token);
      return decoded?.sub || decoded?.email || decoded?.preferred_username || '';
    } catch {
      return '';
    }
  }


  public get currentIdTokenForDisplay(): string {
    try {
      const azureIdToken = this.oidcSecurityService.getIdToken('AZURE');
      if (azureIdToken) {
        return azureIdToken;
      }
      const oktaIdToken = this.oidcSecurityService.getIdToken('OKTA');
      if (oktaIdToken) {
        return oktaIdToken;
      }
      return '';
    } catch (error) {
      return '';
    }
  }

  checkToken(token: string): { valid: boolean; expirationDate: number } | null {
    const decodedToken = this.decodeJwt(token);
    if (decodedToken) {
      return {
        valid: decodedToken.exp >= new Date().getTime() / 1000,
        expirationDate: decodedToken.exp < new Date().getTime() / 1000 ? -1 : decodedToken.exp
      };
    }
    return null;
  }

  localLogOut() {
    this.logOut();
  }

  globalLogOut(url: string): Observable<boolean> {
    return this.requestService.delete(url, { idToken: this.getRefreshToken() }).pipe(
      tap(() => {
        this.logOut();
      }),
      mapTo(true),
      catchError((error) => of(false))
    );
  }

  decodeJwt(token: string): Jwt {
    let jsonPayload = token;

    if (token) {
      const base64Url = token.split('.')[1];
      const base64 = base64Url && base64Url.replace(/-/g, '+').replace(/_/g, '/');
      jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => {
            const str = `00${c.charCodeAt(0).toString(16)}`;
            return `%${str.slice(-2)}`;
          })
          .join('')
      );
    }

    return jsonPayload ? JSON.parse(jsonPayload) : ({} as Jwt);
  }

  getDomimainLogin(email: string) {
    try {
      return email.split('@')[1];
    } catch (error) {
      return '';
    }
  }

  isMapfreDomian(domain: string) {
    return domain?.toLocaleLowerCase().indexOf('mapfre') !== -1;
  }

  loginAzure() {
    this.oidcSecurityService.authorize(OAUTH_AZURE_CONFIG);
  }

  loginOkta(email: string) {
    this.oidcSecurityService.authorize(OAUTH_OKTA_CONFIG, { customParams: email ? { login_hint: email } : {} });
  }

  getRoles(): string[] {
    const roles = sessionStorage.getItem(this.ROLES);
    if (roles) {
      return JSON.parse(roles).userData;
    } else {
      return [];
    }
  }

  checkRolUser(rol: string) {
    return this.getRoles().includes(rol);
  }

  clearLocalStorage() {
    const lan: string | null = localStorage.getItem('langSelect');
    localStorage.clear();
    if (lan) {
      localStorage.setItem('langSelect', lan);
    }
  }

  setUserRole(roles: string[]) {
    sessionStorage.setItem(this.ROLES, JSON.stringify({ userData: roles }));
  }


  public cleanupSessionStorage() {
    this.oidcSecurityService.logoffLocalMultiple();
    this.clearSessionStorage();
    this.clearLocalStorage();
  }

  private logOut() {
    this.menuSrv.clearMenu();
    const configId = this.authType === 'AZURE' ? OAUTH_AZURE_CONFIG : OAUTH_OKTA_CONFIG;
    this.authType = undefined;
    // Limpiar roles y cachés de token antes del logoff: si no, el token del
    // proveedor anterior (OKTA_ROLES_CACHE) se reutiliza al entrar con el otro.
    // 'OKTA'/'AZURE' no se borran aquí: logoff necesita el id_token_hint.
    this.clearRolesCache();
    this.oidcSecurityService.logoff(configId);
  }

  private clearSessionStorage() {
    this.clearRolesCache();
    sessionStorage.removeItem('OKTA');
    sessionStorage.removeItem('AZURE');
  }

  private clearRolesCache() {
    sessionStorage.removeItem(this.ROLES);
    sessionStorage.removeItem('OKTA_ROLES_CACHE');
    sessionStorage.removeItem('AZURE_ROLES_CACHE');
    localStorage.removeItem('OKTA_ROLES_CACHE');
  }

  private getRefreshToken() {
    return this.oidcSecurityService.getRefreshToken();
  }
}
